import React, { Fragment } from 'react';

import {
  calculateDiscountAmount,
  calculateShippingAmount,
  calculateSubTotalAmount,
} from 'businessLogic/server/scsToIcnAdapter';
import InvoiceDataRows from 'components/Core/Invoice/InvoiceDataRows/InvoiceDataRows';
import InvoiceLineItems from 'components/Core/Invoice/InvoiceLineItems/InvoiceLineItems';
import InvoiceSummary from 'components/Core/Invoice/Summary/InvoiceSummary';
import {
  getSurchargeFeeAmountIfEnabled,
  getSurchargeFeePercentageIfEnabled,
} from 'components/Shared/creditCardSurcharging/creditCardSurcharging';
import { useIsMobileScreen } from 'hooks/useIsMobile';
import { useRHSExperiment } from 'hooks/useRHSExperiment';
import { useSelector } from 'store/utils';
import { breakpoints, colors } from 'styles/cp';
import { FeatureFlags } from 'types/FeatureFlags';
import { LineItem } from 'types/Sale';
export type InvoiceInfoProps = {
  invoiceNumber?: string;
  invoiceDueDate?: string;
  currency?: string;
  featureFlags?: FeatureFlags;
  shouldShowInvoiceLineItems?: boolean;
  useRedesign: boolean;
  showModal?: any;
  pdfUrl?: string;
  fetchPDFStatus?: string;
  token?: string;
  gratuityValue?: number;
  isPartiallyPaid?: boolean;
  isFullyPaid?: boolean;
  invoiceLineItems?: LineItem[];
  invoiceTaxAmount?: number;
  invoiceAmount: number;
  achOnlineConvenienceFeeAmount?: number;
  achOnlineConvenienceFeeAmountPaid?: number;
  feeAmount?: number;
  // Breakdown experiment props
  isBreakdownExpanded?: boolean;
  onToggleBreakdown?: () => void;
  // Other experiment props now handled by useRHSExperiment hook
};

const InvoiceInfo = (props: InvoiceInfoProps) => {
  const {
    gratuityValue = 0,
    invoiceNumber,
    invoiceDueDate,
    currency,
    // Breakdown experiment props
    isBreakdownExpanded,
    onToggleBreakdown,
    invoiceAmount,
    useRedesign,
    invoiceLineItems,
    invoiceTaxAmount,
    shouldShowInvoiceLineItems,
    isPartiallyPaid,
    isFullyPaid,
    achOnlineConvenienceFeeAmount,
    achOnlineConvenienceFeeAmountPaid,
  } = props;

  const { isRHSExperimentBreakdownButtonEnabled } = useRHSExperiment();
  const isMobile = useIsMobileScreen(768);

  const discountAmount = calculateDiscountAmount(invoiceLineItems);
  const subTotalAmount = calculateSubTotalAmount(invoiceLineItems);
  const shippingAmount = calculateShippingAmount(invoiceLineItems);

  const hrColor = isMobile ? colors.gray06 : colors.offWhite;

  // Read CC surcharge values using shared helpers (feature flag + experiment aware)
  const featureFlags = useSelector((state) => state.featureFlags);
  const sale = useSelector((state) => state.sale);
  const ccSurchargingFeeAmount = getSurchargeFeeAmountIfEnabled({ featureFlags, sale });
  const ccSurchargingFeePercentage = getSurchargeFeePercentageIfEnabled({ featureFlags, sale });
  return (
    <Fragment>
      <style jsx>{`
        .invoice-info {
          padding: 0px;
          margin: ${useRedesign ? '0 14px' : '0'};

          @media (max-width: ${breakpoints.md}) {
            max-width: 400px;
            margin: 5px auto;
          }
          @media (max-width: ${breakpoints.sm}) {
            max-width: 576px;
          }
        }
      `}</style>

      <ul className="invoice-info" aria-label="invoice info list">
        <InvoiceSummary
          invoiceNumber={invoiceNumber}
          invoiceDueDate={invoiceDueDate}
          invoiceAmount={invoiceAmount}
          shouldShowInvoiceLineItems={shouldShowInvoiceLineItems}
          currency={currency}
          gratuityValue={gratuityValue}
          hrColor={hrColor}
          // Experiment props removed - now handled by useRHSExperiment hook
        />
        {shouldShowInvoiceLineItems && !isRHSExperimentBreakdownButtonEnabled && (
          <InvoiceLineItems
            invoiceLineItems={invoiceLineItems}
            currency={currency}
            subTotalAmount={subTotalAmount}
            discountAmount={discountAmount}
            shippingAmount={shippingAmount}
            invoiceTaxAmount={invoiceTaxAmount}
            hrColor={hrColor}
          />
        )}
        <InvoiceDataRows
          currency={currency}
          isPartiallyPaid={isPartiallyPaid}
          isFullyPaid={isFullyPaid}
          invoiceAmount={invoiceAmount}
          achOnlineConvenienceFeeAmount={achOnlineConvenienceFeeAmount}
          achOnlineConvenienceFeeAmountPaid={achOnlineConvenienceFeeAmountPaid}
          ccSurchargingFeeAmount={ccSurchargingFeeAmount}
          ccSurchargingFeePercentage={ccSurchargingFeePercentage}
          // Breakdown experiment props
          isBreakdownExpanded={isBreakdownExpanded}
          onToggleBreakdown={onToggleBreakdown}
          // Line items data for breakdown treatment
          invoiceLineItems={invoiceLineItems}
          subTotalAmount={subTotalAmount}
          discountAmount={discountAmount}
          shippingAmount={shippingAmount}
          invoiceTaxAmount={invoiceTaxAmount}
          hrColor={hrColor}
          // Experiment and mobile state now handled by useRHSExperiment hook
        />
      </ul>
    </Fragment>
  );
};

export default InvoiceInfo;
